import React from 'react';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { Eye, EyeOff, Lock } from 'lucide-react';
import API_URL from '../../api/baseURL';

export default function AdminFeatureFlags() {
  const { token } = useAuth();
  const { features, isLoading, invalidate } = useFeatureFlags();

  const mutation = useMutation({
    mutationFn: async ({ key, enabled }) => {
      await axios.put(
        `${API_URL}/admin/feature-flags`,
        { features: { [key]: { enabled } } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    onSuccess: () => invalidate(),
  });

  const handleToggle = (key) => {
    if (key === 'dashboard') return; // cannot disable dashboard
    const current = features[key]?.enabled !== false;
    mutation.mutate({ key, enabled: !current });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400" />
      </div>
    );
  }

  const featureKeys = Object.keys(features);

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <Eye className="w-7 h-7 text-cyan-400" />
          Visibilité des fonctions
        </h1>
        <p className="text-slate-400 mt-1 text-sm">
          Contrôlez les fonctions visibles pour les propriétaires de véhicules.
          Les fonctions désactivées sont masquées du menu et inaccessibles.
        </p>
      </div>

      <div className="space-y-2">
        {featureKeys.map((key) => {
          const feature = features[key];
          const enabled = feature?.enabled !== false;
          const isDashboard = key === 'dashboard';

          return (
            <div
              key={key}
              className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
                enabled
                  ? 'bg-slate-800/60 border-slate-700/50'
                  : 'bg-slate-900/60 border-slate-800/50 opacity-60'
              }`}
            >
              <div className="flex items-center gap-3">
                {enabled ? (
                  <Eye className="w-4 h-4 text-cyan-400" />
                ) : (
                  <EyeOff className="w-4 h-4 text-slate-500" />
                )}
                <div>
                  <span className={`text-sm font-medium ${enabled ? 'text-white' : 'text-slate-400'}`}>
                    {feature?.label || key}
                  </span>
                  {isDashboard && (
                    <span className="ml-2 inline-flex items-center gap-1 text-xs text-amber-400">
                      <Lock className="w-3 h-3" /> Toujours actif
                    </span>
                  )}
                </div>
              </div>

              <button
                onClick={() => handleToggle(key)}
                disabled={isDashboard || mutation.isPending}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  isDashboard
                    ? 'bg-slate-600 cursor-not-allowed'
                    : enabled
                      ? 'bg-cyan-600 hover:bg-cyan-500 cursor-pointer'
                      : 'bg-slate-600 hover:bg-slate-500 cursor-pointer'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          );
        })}
      </div>

      {mutation.isError && (
        <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          Erreur lors de la mise à jour. Veuillez réessayer.
        </div>
      )}
    </div>
  );
}
